/** React Query key prefix for dealer onboarding progress (shared across landing + enrollment). */
export const DEALER_ONBOARDING_PROGRESS_QUERY_KEY = "dealerOnboardingProgress";

/** API sentinel when preference is not set (see DealerOnboardingService / preferences DTO). */
export const PREFERENCE_UNSET = "UNSET";

function mapYesNoPreferenceToRadioSelection(apiValue) {
  if (apiValue == null || typeof apiValue !== "string") {
    return "";
  }
  const trimmed = apiValue.trim();
  if (!trimmed || trimmed.toUpperCase() === PREFERENCE_UNSET) {
    return "";
  }
  const lower = trimmed.toLowerCase();
  if (lower === "yes") {
    return "Yes";
  }
  if (lower === "no") {
    return "No";
  }
  return "";
}

/** Canonical values for `preferences.vehiclePricingPreference` (API / DB). */
export const VEHICLE_PRICING_VALUE_VL_PLUS = "vl_plus";
export const VEHICLE_PRICING_VALUE_IMPORT_THIRD_PARTY = "import_third_party";

/**
 * Maps progress API `preferences.vehiclePricingPreference` to radio `value` strings.
 * @param {string | null | undefined} apiValue
 * @returns {"" | "vl_plus" | "import_third_party"}
 */
export function mapProgressVehiclePricingToRadioSelection(apiValue) {
  if (apiValue == null || typeof apiValue !== "string") {
    return "";
  }
  const trimmed = apiValue.trim();
  if (!trimmed || trimmed.toUpperCase() === PREFERENCE_UNSET) {
    return "";
  }
  const lower = trimmed.toLowerCase();
  if (lower === VEHICLE_PRICING_VALUE_VL_PLUS) {
    return VEHICLE_PRICING_VALUE_VL_PLUS;
  }
  if (lower === VEHICLE_PRICING_VALUE_IMPORT_THIRD_PARTY) {
    return VEHICLE_PRICING_VALUE_IMPORT_THIRD_PARTY;
  }
  return "";
}

/**
 * Maps progress API `preferences.routeOneId` to dropdown `value` string.
 * @param {string | null | undefined} apiValue
 * @returns {string} The RouteOne Dealer ID, or empty string if not set
 */
export function mapRouteOneIdPreferenceToDropDownSelection(apiValue) {
  if (apiValue == null || typeof apiValue !== "string") {
    return "";
  }
  const trimmed = apiValue.trim();
  if (!trimmed || trimmed.toUpperCase() === PREFERENCE_UNSET) {
    return "";
  }
  return trimmed;
}

/**
 * Maps progress API `preferences.buyNowPreference` to radio `value` strings.
 * @param {string | null | undefined} apiValue
 * @returns {"" | "Yes" | "No"}
 */
export function mapProgressBuyNowToRadioSelection(apiValue) {
  return mapYesNoPreferenceToRadioSelection(apiValue);
}

/** Canonical values for `preferences.tradeInPreference` (API / DB). */
export const TRADE_IN_VALUE_ESTIMATED = "ESTIMATED_VALUE";
export const TRADE_IN_VALUE_LINCOLN_BACKED = "LINCOLN_BACKED_VALUE";

/**
 * Maps progress API `preferences.tradeInPreference` to radio `value` strings.
 * @param {string | null | undefined} apiValue
 * @returns {"" | "ESTIMATED_VALUE" | "LINCOLN_BACKED_VALUE"}
 */
export function mapProgressTradeInToRadioSelection(apiValue) {
  if (apiValue == null || typeof apiValue !== "string") {
    return "";
  }
  const trimmed = apiValue.trim();
  if (!trimmed || trimmed.toUpperCase() === PREFERENCE_UNSET) {
    return "";
  }
  const upper = trimmed.toUpperCase();
  if (upper === TRADE_IN_VALUE_ESTIMATED) {
    return TRADE_IN_VALUE_ESTIMATED;
  }
  if (upper === TRADE_IN_VALUE_LINCOLN_BACKED) {
    return TRADE_IN_VALUE_LINCOLN_BACKED;
  }
  return "";
}

/**
 * Maps progress API `preferences.advertisedPlanPricingPreference` to radio `value` strings.
 * @param {string | null | undefined} apiValue
 * @returns {"" | "Yes" | "No"}
 */
export function mapProgressAdvertisedPlanPricingToRadioSelection(apiValue) {
  return mapYesNoPreferenceToRadioSelection(apiValue);
}

/**
 * Maps progress API `preferences.stripePreference` to radio values ("yes" | "no").
 * @param {string | null | undefined} apiValue
 * @returns {"" | "yes" | "no"}
 */
export function mapProgressStripePreferenceToRadioSelection(apiValue) {
  const mapped = mapYesNoPreferenceToRadioSelection(apiValue);
  if (mapped === "Yes") {
    return "yes";
  }
  if (mapped === "No") {
    return "no";
  }
  return "";
}

/** Matches dbo.steps / API stepName for Stripe enrollment. */
export const STRIPE_ENROLLMENT_STEP_NAME = "Stripe enrollment";

/** Stripe Setup / preference substep name from the onboarding progress API. */
export const STRIPE_PREFERENCE_SUBSTEP_NAME = "Stripe preference";

/** Client Deposit Amounts substep name from the onboarding progress API. */
export const STRIPE_DEPOSIT_AMOUNTS_SUBSTEP_NAME = "Stripe deposit amounts";

/**
 * Finds the Stripe preference substep from progress steps.
 * @param {Array} steps
 * @returns {object | null}
 */
export function findStripePreferenceSubstep(steps) {
  if (!Array.isArray(steps)) {
    return null;
  }
  const stripeStep = steps.find(
    (step) => step.stepName === STRIPE_ENROLLMENT_STEP_NAME
  );
  if (!stripeStep?.substeps?.length) {
    return null;
  }
  return (
    stripeStep.substeps.find(
      (substep) => substep.substepName === STRIPE_PREFERENCE_SUBSTEP_NAME
    ) ?? null
  );
}

/**
 * Finds the Stripe deposit amounts substep from progress steps.
 * @param {Array} steps
 * @returns {object | null}
 */
export function findStripeDepositAmountsSubstep(steps) {
  if (!Array.isArray(steps)) {
    return null;
  }
  const stripeStep = steps.find(
    (step) => step.stepName === STRIPE_ENROLLMENT_STEP_NAME
  );
  if (!stripeStep?.substeps?.length) {
    return null;
  }
  return (
    stripeStep.substeps.find(
      (substep) => substep.substepName === STRIPE_DEPOSIT_AMOUNTS_SUBSTEP_NAME
    ) ?? null
  );
}

/** Matches dbo.steps / API stepName for EMP onboarding (Enrollment). */
export const ENROLLMENT_STEP_NAME = "Enrollment";

/** API `stepName` values for dealer onboarding landing tabs (must match `/progress`). */
export const ONBOARDING_LANDING_TAB_TO_STEP_NAME = {
  "onboardingSteps-enrollment": ENROLLMENT_STEP_NAME,
  "onboardingSteps-entitlements": "Entitlements",
  "onboardingSteps-tradeIn": "Trade-In",
  "onboardingSteps-paymentProcessorOnlineDeposits": "Stripe enrollment",
  "onboardingSteps-vehiclePricing": "Vehicle pricing",
  "onboardingSteps-accessoryPricing": "Accessory pricing",
  "onboardingSteps-thirdPartyFinancing": "Third Party Financing",
  "onboardingSteps-protectionPlans": "Protection plans",
  "onboardingSteps-tierThreeBuyNow": "Buy Now",
  "onboardingSteps-advertisedPlanPricing": "Advertised plan pricing",
};

/** Matches dbo.steps / API stepName for the Vehicle Pricing onboarding step. */
export const VEHICLE_PRICING_STEP_NAME = "Vehicle pricing";

/** Vehicle Pricing substep name from the onboarding progress API. */
export const VEHICLE_PRICING_METHOD_SUBSTEP_NAME = "Vehicle pricing method";

/**
 * Finds the Vehicle Pricing top-level step from the onboarding progress API.
 *
 * @param {Array} steps - `DealerOnboardingProgressResponse.steps`
 * @returns {object | null}
 */
export function findVehiclePricingStep(steps) {
  if (!Array.isArray(steps)) {
    return null;
  }

  return (
    steps.find((step) => step.stepName === VEHICLE_PRICING_STEP_NAME) ?? null
  );
}

/**
 * Finds the Vehicle Pricing → Vehicle pricing method substep.
 *
 * @param {Array} steps - `DealerOnboardingProgressResponse.steps`
 * @returns {object | null}
 */
export function findVehiclePricingSubstep(steps) {
  const vehiclePricingStep = findVehiclePricingStep(steps);

  if (!vehiclePricingStep?.substeps?.length) {
    return null;
  }

  return (
    vehiclePricingStep.substeps.find(
      (substep) => substep.substepName === VEHICLE_PRICING_METHOD_SUBSTEP_NAME
    ) ?? null
  );
}

/**
 * Resolves API IDs required by `/dealers/onboarding/substep-save` for
 * Vehicle Pricing → Vehicle pricing method.
 *
 * @param {Array} steps - `DealerOnboardingProgressResponse.steps`
 * @returns {{ stepId: number, substepId: number } | null}
 */
export function findVehiclePricingSubstepIds(steps) {
  const vehiclePricingStep = findVehiclePricingStep(steps);
  const vehiclePricingSubstep = findVehiclePricingSubstep(steps);

  const stepId = vehiclePricingStep?.stepId;
  const substepId = vehiclePricingSubstep?.substepId;

  if (stepId == null || substepId == null) {
    return null;
  }

  return {
    stepId,
    substepId,
  };
}

/**
 * @param {Array<{ stepName?: string, status?: string }> | null | undefined} steps - from `/progress`
 * @param {string} tabId - e.g. {@code onboardingSteps-enrollment}
 * @returns {string | null} API step `status`, or null if unknown / no row
 */
export function getOnboardingStepStatusForLandingTab(steps, tabId) {
  const stepName = ONBOARDING_LANDING_TAB_TO_STEP_NAME[tabId];
  if (!stepName || !Array.isArray(steps)) {
    return null;
  }
  const row = steps.find((s) => s.stepName === stepName);
  return row?.status ?? null;
}

/**
 * @param {Array<{ stepName?: string, status?: string }> | null | undefined} steps
 * @param {string} tabId
 * @returns {boolean}
 */
export function isOnboardingLandingTabCompleted(steps, tabId) {
  const status = getOnboardingStepStatusForLandingTab(steps, tabId);
  return typeof status === "string" && status.toLowerCase() === "completed";
}

/** First substep under Enrollment — terms acceptance (dbo.substeps.name). */
export const TERMS_AND_CONDITIONS_SUBSTEP_NAME = "Terms & Conditions";

/** Other Enrollment substeps (API `substepName` values). */
export const ENROLLMENT_SUBSTEP_DPES = "DPES Enrollment";
export const ENROLLMENT_SUBSTEP_DEALERSHIP_INFO = "Dealership Info";
export const ENROLLMENT_SUBSTEP_IN_DEALERSHIP_TEAM = "In Dealership Team";

/**
 * Enrollment substep row from progress API by `substepName`.
 * @param {Array} steps - `DealerOnboardingProgressResponse.steps`
 * @param {string} substepName - e.g. {@link TERMS_AND_CONDITIONS_SUBSTEP_NAME}
 * @returns {object | null}
 */
export function findEnrollmentSubstep(steps, substepName) {
  if (!Array.isArray(steps) || !substepName) {
    return null;
  }
  const enrollment = steps.find((s) => s.stepName === ENROLLMENT_STEP_NAME);
  if (!enrollment?.substeps?.length) {
    return null;
  }
  return (
    enrollment.substeps.find((ss) => ss.substepName === substepName) ?? null
  );
}

/**
 * Resolves API step/substep ids for the Enrollment → Terms & Conditions substep.
 * @param {Array<{ stepId?: number, stepName?: string, substeps?: Array<{ substepId?: number, substepName?: string }> }>} steps - from DealerOnboardingProgressResponse.steps
 * @returns {{ stepId: number, substepId: number } | null}
 */
export function findEnrollmentTermsSubstepIds(steps) {
  if (!Array.isArray(steps)) {
    return null;
  }
  const enrollment = steps.find((s) => s.stepName === ENROLLMENT_STEP_NAME);
  const terms = findEnrollmentSubstep(steps, TERMS_AND_CONDITIONS_SUBSTEP_NAME);
  if (!enrollment?.substeps?.length || !terms) {
    return null;
  }
  const stepId = enrollment.stepId;
  const substepId = terms.substepId;
  if (stepId == null || substepId == null) {
    return null;
  }
  return { stepId, substepId };
}

/**
 * Enrollment → Terms & Conditions substep row from progress API (includes status, completedAt, updatedBy).
 * @param {Array} steps
 * @returns {object | null}
 */
export function findEnrollmentTermsSubstep(steps) {
  return findEnrollmentSubstep(steps, TERMS_AND_CONDITIONS_SUBSTEP_NAME);
}

/** Matches dbo.steps / API stepName for EMP onboarding (Entitlements). */
export const ENTITLEMENTS_STEP_NAME = "Entitlements";

/** Entitlements substeps (API `substepName` values). */
export const ENTITLEMENTS_SUBSTEP_KDM = "Key Decision Makers (KDMs)";
export const ENTITLEMENTS_SUBSTEP_VEHICLE_PRICING =
  "Vehicle Pricing Entitlements";
export const ENTITLEMENTS_SUBSTEP_ACCESSORY_PRICING =
  "Accessory Pricing Entitlements";
export const ENTITLEMENTS_SUBSTEP_CUSTOMER_HANDLING =
  "Customer Handling Entitlements";

/**
 * Entitlements substep row from progress API by `substepName`.
 * @param {Array} steps - `DealerOnboardingProgressResponse.steps`
 * @param {string} substepName - e.g. {@link ENTITLEMENTS_SUBSTEP_KDM}
 * @returns {object | null}
 */
export function findEntitlementsSubstep(steps, substepName) {
  if (!Array.isArray(steps) || !substepName) {
    return null;
  }
  const entitlements = steps.find((s) => s.stepName === ENTITLEMENTS_STEP_NAME);
  if (!entitlements?.substeps?.length) {
    return null;
  }
  return (
    entitlements.substeps.find((ss) => ss.substepName === substepName) ?? null
  );
}

/**
 * @param {Array} steps - `DealerOnboardingProgressResponse.steps`
 * @param {string} substepName - e.g. {@link ENTITLEMENTS_SUBSTEP_KDM}
 * @returns {boolean}
 */
export function isEntitlementsSubstepCompleted(steps, substepName) {
  const status = findEntitlementsSubstep(steps, substepName)?.status;
  return typeof status === "string" && status.toLowerCase() === "completed";
}

/**
 * @param {string|number|Date} completedAt - ISO date string from API
 * @returns {string} e.g. 12/12/2025 14:07 (UTC)
 */
export function formatTermsAcceptedDate(completedAt) {
  if (completedAt == null || completedAt === "") {
    return "";
  }
  const d = new Date(completedAt);
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  // Use UTC so API ISO timestamps display consistently regardless of browser locale/timezone.
  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  const year = d.getUTCFullYear();
  const hours = String(d.getUTCHours()).padStart(2, "0");
  const minutes = String(d.getUTCMinutes()).padStart(2, "0");
  return `${month}/${day}/${year} ${hours}:${minutes}`;
}

/**
 * Formats an onboarding step completedAt timestamp as a date only (no time).
 * @param {string|number|Date} completedAt - ISO date string from API
 * @returns {string} e.g. 12/12/2025 (UTC)
 */
export function formatOnboardingCompletedDate(completedAt) {
  if (completedAt == null || completedAt === "") {
    return "";
  }
  const d = new Date(completedAt);
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  const year = d.getUTCFullYear();
  return `${month}/${day}/${year}`;
}
